import React, { useState, useEffect } from "react";
import {
  DialogContainer,
  FontIcon,
  CircularProgress,
} from "react-md";

import CartSelectPaymentMethod from "./cartPaymentStates/CartSelectPaymentMethod";
import CartSelectShareLinkOptions from "./cartPaymentStates/CartSelectShareLinkOptions";
import CartSuccessWithGeneratedLink from "./cartPaymentStates/CartSuccessWithGeneratedLink";
import CartSuccessWithoutLink from "./cartPaymentStates/CartSuccessWithoutLink";
import CartErrorInApprove from "./cartPaymentStates/CartErrorInApprove";

import {
  CART_SCREEN_STATE,
  cartNeedsCollection,
  formatCurrency,
  getCartGrandTotalForMethod,
  getCartPaymentDetails,
} from "./cartPaymentLinkConstants";
import CartDashboardUtils from "../../services/CartDashboardUtils";
import EventService from "../../../../../../service/event/EventService";
import "../../../navigo-module/styles/PaymentLinkDialog.css";

/**
 * Cart approve / payment-link flow (VERIFY mode). Opened from
 * CartPaymentDetailsDialog's "Continue". Driven entirely by the
 * /payment-sublimit preview already fetched by the parent:
 *
 *   boot ─┬─ has collection ─→ SELECT_PAYMENT_METHOD ─┬─ needs link ─→ SELECT_SHARE_LINK_OPTIONS ─→ approve ─→ SUCCESS_WITH_GENERATED_LINK
 *         │                                            └─ no link ────→ approve ─→ SUCCESS_WITHOUT_LINK
 *         └─ no collection (refund-only / no-op) ─────────────────────→ approve ─→ SUCCESS_WITHOUT_LINK
 *
 * The approve call is POST /api/v1/cart/admin/{cartId}/approve. `useWallet`
 * (from the chosen option) is the source of truth the backend reads; the
 * `paymentDetails` we send is the preview's sublimit object verbatim (the
 * backend re-verifies it when useWallet=true). On success, "Done" opens the
 * created appointment (DocumentUpload view).
 */
const CartPaymentLinkDialog = ({
  visible = false,
  onClose = () => {},
  preview = null,
  cartId,
  props,
  profileDetails = null,
  onApproved = () => {},
}) => {
  const [screenState, setScreenState] = useState(-1);
  const [paymentMethod, setPaymentMethod] = useState(-1);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approveResponse, setApproveResponse] = useState(null);
  const [linkBody, setLinkBody] = useState(null);
  const [errText, setErrText] = useState("");

  const cartDashboardUtils = new CartDashboardUtils();

  // Boot: decide whether to show method selection or approve straight away
  // (refund-only / no-op carts have nothing to collect → no choice to make).
  useEffect(() => {
    if (!visible || screenState !== -1) return;

    if (!preview || !cartId) {
      EventService.emit("showError", {
        message: "Cart payment details missing, cannot continue",
      });
      onClose();
      return;
    }

    if (cartNeedsCollection(preview)) {
      setScreenState(CART_SCREEN_STATE.SELECT_PAYMENT_METHOD);
    } else {
      // Nothing to collect (refund-only / no-op) — approve directly and
      // preserve prior wallet usage (useWallet=true).
      approveCart(true, null, false);
    }
  }, [visible, screenState]);

  const approveCart = (useWallet, paymentLink, expectLink) => {
    setLoading(true);

    const body = {
      expectedHeadRevision: preview?.headRevision ?? 0,
      reason: "",
      paymentLinkRequest: {
        // Only the 3 fields the backend reads, sourced by useWallet: the
        // sublimit split for wallet paths, the no-wallet alternative's net
        // top-up for Razorpay-only (with a sublimit fallback / {0,0,0} refund).
        paymentDetails: getCartPaymentDetails(preview, useWallet),
        paymentLink: paymentLink || {},
        useWallet: !!useWallet,
      },
    };

    const onResponse = (res) => {
      setLoading(false);
      setApproveResponse(res);

      // Cart is now approved — refresh the verification dashboard's data (it is
      // still mounted behind this screen) so the list reflects the new status.
      onApproved();

      if (expectLink) {
        const responseLink = res?.paymentDetails?.paymentLink;
        // Prefer link fields from the response; keep the channels we submitted.
        setLinkBody({ ...(paymentLink || {}), ...(responseLink || {}) });
        setScreenState(CART_SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK);
      } else {
        setScreenState(CART_SCREEN_STATE.SUCCESS_WITHOUT_LINK);
      }
    };

    const onError = (err) => {
      setLoading(false);
      setErrText(err?.message || "Something went wrong");
      setScreenState(CART_SCREEN_STATE.ERROR_IN_APPROVE);
      EventService.emit("showError", {
        message: `Error: ${err?.message || "Something went wrong"}`,
      });
    };

    cartDashboardUtils.approveCart(cartId, body, onResponse, onError);
  };

  // From CartSelectPaymentMethod: either advance to share-link or approve now.
  const goNextFromMethod = (screenNo, option) => {
    setSelectedOption(option);
    if (screenNo === CART_SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS) {
      setScreenState(CART_SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS);
    } else {
      approveCart(option.useWallet, null, false);
    }
  };

  // From CartSelectShareLinkOptions: approve with the chosen channels.
  const goNextFromShare = (_screenNo, paymentLink) => {
    approveCart(selectedOption?.useWallet, paymentLink, true);
  };

  const handleDone = () => {
    const appId = approveResponse?.appointmentId;
    if (appId && props?.setView) {
      props.setView(2, { appId });
    } else {
      onClose();
    }
  };

  const isWideScreen =
    screenState === CART_SCREEN_STATE.SELECT_PAYMENT_METHOD ||
    screenState === CART_SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS;

  return (
    <DialogContainer
      id="cart-payment-link-dialog"
      visible={visible}
      onHide={() => {}}
      focusOnMount={false}
      autosizeContent
      width={isWideScreen ? "720px" : "550px"}
      dialogStyle={{ borderRadius: "10px", minHeight: "400px" }}
    >
      {loading ? (
        <div style={{ marginTop: "160px" }}>
          <CircularProgress id="cart-payment-link-progress" />
        </div>
      ) : (
        <div className="payment-link-dialog-container zy-text-container">
          {/* Header (only on input screens) */}
          {isWideScreen && (
            <>
              <div className="pld-header">
                <div className="pld-header-title">Proceed to pay</div>
                <FontIcon className="pld-close-icon" onClick={onClose}>
                  close
                </FontIcon>
              </div>
              <div className="pld-total-wrapper">
                <div className="pld-total-box">
                  Grand total:{" "}
                  <span className="pld-bold">
                    {formatCurrency(
                      getCartGrandTotalForMethod(preview, paymentMethod),
                    )}
                  </span>
                </div>
              </div>
            </>
          )}

          {screenState === CART_SCREEN_STATE.SELECT_PAYMENT_METHOD && (
            <CartSelectPaymentMethod
              preview={preview}
              paymentMethod={paymentMethod}
              setPaymentMethod={setPaymentMethod}
              goNext={goNextFromMethod}
            />
          )}

          {screenState === CART_SCREEN_STATE.SELECT_SHARE_LINK_OPTIONS && (
            <CartSelectShareLinkOptions
              goNext={goNextFromShare}
              profileDetails={profileDetails}
            />
          )}

          {screenState === CART_SCREEN_STATE.SUCCESS_WITH_GENERATED_LINK && (
            <CartSuccessWithGeneratedLink
              onDone={handleDone}
              linkBody={linkBody}
              profileDetails={profileDetails}
            />
          )}

          {screenState === CART_SCREEN_STATE.SUCCESS_WITHOUT_LINK && (
            <CartSuccessWithoutLink
              onDone={handleDone}
              approveResponse={approveResponse}
            />
          )}

          {screenState === CART_SCREEN_STATE.ERROR_IN_APPROVE && (
            <CartErrorInApprove
              onClose={() => {
                setScreenState(-1);
                onClose();
              }}
              errorText={errText}
            />
          )}
        </div>
      )}
    </DialogContainer>
  );
};

export default CartPaymentLinkDialog;
